import { useToggle } from 'react-use';
import React, { useCallback, useMemo, useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  GetIncidentActionResponse,
  useIncidentActions,
  useUpdateIncidentAction,
} from 'components/Actions/hooks/incidentActionHooks';

import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  useFutureOperationalPeriodNumber,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { useSearchParams } from 'react-router';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { ColumnDef } from '@tanstack/react-table';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import ActionAddDrawer from 'components/Actions/OpenActionAddDrawer';
import ActionEditDrawer from 'components/Actions/OpenActionEditDrawer';
import ActionExpandedRow from 'components/Actions/ActionExpandedRow';
import ExpandDownArrow from 'components/DesignSystems/buttons/ExpandDownArrow';
import ExpandRightArrow from 'components/DesignSystems/buttons/ExpandRightArrow';
import ICSWidget from './ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';

type Option = {
  label: string;
  value: string;
};
// const STATUS_OPTIONS: Option[] = [
//   { label: 'Not Started', value: 'Not Started' },
//   { label: 'In Progress', value: 'In Progress' },
//   { label: 'Completed', value: 'Completed' },
//   { label: 'Blocked', value: 'Blocked' },
// ];

const headerCell = (label: string) => (
  <span className="text-nowrap">{label}</span>
);

const OpenActionsTracker: React.FC = () => {
  const [addOpen, toggleAddDrawer] = useToggle(false);
  const [editOpen, toggleEditDrawer] = useToggle(false);
  const { data: incidentActions } = useIncidentActions();
  const { exportAsForm } = useIcsFormPdfPreview();
  const futurePeriodNumber = useFutureOperationalPeriodNumber();
  const { data: roster } = useGetRosterForIncident();
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-open-actions-tracker`;
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { mutateAsync: updateIncidentAction } = useUpdateIncidentAction();

  const [selectedAction, setSelectedAction] = useState<any>(null);

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const rosterOptions = useMemo<Option[]>(
    () =>
      roster?.map((member) => ({
        label: member.userName || (member.emailAddress as string),
        value: member.userId,
      })) ?? [],
    [roster]
  );

  const handleExport = () => {
    void exportAsForm({
      formname: 'ICS_233_CG',
      data: {},
      mapSketch: null,
      periodNumber: futurePeriodNumber,
    });
  };

  const handleAddBlankAction = async () => {
    toggleAddDrawer(true);
  };

  const rows = useMemo(
    () =>
      (incidentActions ?? [])
        .filter((action) => !action.archived)
        .filter((a) =>
          search.trim()
            ? (a.name ?? '').toLowerCase().includes(search.trim().toLowerCase())
            : true
        ),
    [incidentActions, search]
  );

  const allSelected = useMemo(() => {
    if (!rows.length) return false;
    return rows.length === selectedIds.length;
  }, [rows, selectedIds]);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(allSelected ? [] : rows.map((row) => row.id));
  }, [allSelected, rows]);

  const toggleRow = useCallback(
    (id: string) => {
      const set = new Set(selectedIds);
      if (set.has(id)) {
        set.delete(id);
      } else {
        set.add(id);
      }
      setSelectedIds([...set]);
    },
    [selectedIds]
  );

  const deleteSelected = async () => {
    const toArchive = rows.filter((r) => selectedIds.includes(r.id));
    await Promise.all(
      toArchive.map((action) =>
        updateIncidentAction({ ...action, archived: true })
      )
    );
    setSelectedIds([]);
  };

  const deleteRow = useCallback(
    async (row: GetIncidentActionResponse) => {
      await updateIncidentAction({ ...row, archived: true });
      setSelectedIds((prev) => prev.filter((x) => x !== row.id));
    },
    [updateIncidentAction]
  );

  const columns = useMemo<ColumnDef<GetIncidentActionResponse>[]>(
    () => [
      {
        id: 'expander',
        cell: ({ row }) => {
          const expandable = row.getCanExpand?.();
          if (!expandable) return null;
          return row.getIsExpanded() ? (
            <ExpandDownArrow onClick={() => row.toggleExpanded()} />
          ) : (
            <ExpandRightArrow onClick={() => row.toggleExpanded()} />
          );
        },
        meta: { customSize: true, headerAlign: 'center', align: 'center' },
        size: 42,
      },
      {
        id: 'select',
        header: () => (
          <StylishNewCheckbox
            checked={allSelected}
            onChange={toggleSelectAll}
          />
        ),
        cell: ({ row }) => (
          <StylishNewCheckbox
            checked={selectedIds.includes(row.original.id)}
            onChange={() => toggleRow(row.original.id)}
          />
        ),
        size: 40,
        enableSorting: false,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'name',
        header: () => headerCell('Task'),
        cell: ({ getValue }) => {
          return <span>{getValue<string>() || ''}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'poc',
        header: () => headerCell('Point of Contact'),
        cell: ({ row, getValue }) => {
          const pocVal = getValue<string>();
          const label =
            rosterOptions.find((o) => o.value === pocVal)?.label ||
            row.original.poc_user_name ||
            pocVal ||
            '';
          return <span>{label}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'briefed_poc',
        header: () => headerCell('POC Briefed'),
        cell: ({ row, getValue }) => (
          <StylishNewCheckbox
            checked={getValue<boolean>()}
            disabled={!canEditCards}
            onClick={() => {
              if (!canEditCards) return;
              updateIncidentAction({
                ...row.original,
                briefed_poc: !getValue(),
              });
            }}
          />
        ),
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'start_date',
        header: () => headerCell('Start Date'),
        cell: ({ getValue }) => {
          const value = getValue<Date | null>();
          const text = value ? new Date(value).toLocaleDateString() : '';
          return <span>{text}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'target_date',
        header: () => headerCell('Deadline'),
        cell: ({ getValue }) => {
          const value = getValue<Date | null>();
          const text = value ? new Date(value).toLocaleDateString() : '';
          return <span>{text}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        accessorKey: 'status',
        header: () => headerCell('Status'),
        cell: ({ getValue }) => {
          return <span>{getValue<string>() || ''}</span>;
        },
        enableSorting: true,
        meta: { headerAlign: 'left' },
      },
      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => {
          if (!canEditCards) return null;
          return (
            <div className="d-flex gap-1">
              <StylishNewButton
                className="button--secondary border-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAction(row.original);
                  toggleEditDrawer(true);
                }}
              >
                <SharedIcon iconName={'edit'} classes="me-1" />
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary border-0"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteRow(row.original);
                }}
              >
                <SharedIcon iconName="delete" classes="me-1" />
              </StylishNewButton>
            </div>
          );
        },
        enableSorting: false,
        size: 160,
        meta: { headerAlign: 'left' },
      },
    ],
    [
      allSelected,
      canEditCards,
      updateIncidentAction,
      rosterOptions,
      selectedIds,
      toggleRow,
      toggleSelectAll,
      deleteRow,
      toggleEditDrawer,
    ]
  );

  return (
    <>
      <ConditionalCollapsibleCard
        sectionKey={sectionKey}
        className="planning-widget"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="checklist"
            iconColor="primary"
            title={getIncidentCardTitle('openActionsTracker')}
            subtitle="Track action items and follow-ups from the meeting"
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            reviewActionKey="actions"
            icsRoles={['PSC']}
            handleExport={handleExport}
            exportText="Preview ICS-233CG"
          />
        }
      >
        <div className="d-flex justify-content-between align-items-end mb-2 flex-wrap gap-2">
          <div className="flex-grow-1" style={{ minWidth: 240 }}>
            <input
              className="form-control"
              placeholder="Search actions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEditCards && (
            <div>
              <StylishNewButton
                className="button button--primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddBlankAction();
                }}
              >
                <SharedIcon iconName="add" classes="me-2" />
                Add Action
              </StylishNewButton>
            </div>
          )}
        </div>

        {canEditCards && selectedIds.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>{selectedIds.length} selected</div>
            <StylishNewButton
              className="button--error"
              onClick={(e) => {
                e.stopPropagation();
                deleteSelected();
              }}
            >
              <SharedIcon iconName="delete" classes="me-1" /> Delete Selected
            </StylishNewButton>
          </div>
        )}
        <PratusTable
          className="layout-auto pratus-table"
          columns={columns}
          data={rows}
          getRowId={(row) => row.id}
          expansion={{
            getRowCanExpand: () => true,
            singleExpansion: true,
            expandedRowModel: (row) => <ActionExpandedRow row={row} />,
          }}
          noDataIndication={
            <div className="p-2 text-center">
              {search
                ? 'No results!'
                : 'There are no actions yet. Click + Add Action to add an action.'}
            </div>
          }
        />
      </ConditionalCollapsibleCard>
      {canEditCards && addOpen ? (
        <ActionAddDrawer toggle={toggleAddDrawer} />
      ) : null}
      {canEditCards && editOpen && selectedAction ? (
        <ActionEditDrawer
          selectedEntity={selectedAction}
          toggle={toggleEditDrawer}
        />
      ) : null}
    </>
  );
};

export default OpenActionsTracker;
